import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { fetchPreviousCalls } from './thunks';

import type { RootState } from 'features/app/store/store';
import { closeCall, handleResetCall } from 'features/call/call-base/store';
import {
  callIdSelector,
  isHearingToDeafSelector,
  terminationTypeSelector,
} from 'features/call/call-base/store/selectors';
import { CallTimerDaoService } from 'features/call/call-timer/services';
import {
  PreviousCallReportSource,
  ReportType,
} from 'features/call/vrs-call-comments/enums';
import type { VrsCallCommentsState } from 'features/call/vrs-call-comments/interfaces';
import { terminationDeciderVrs } from 'features/call/vrs-call/helpers';
import { resetCallWasPlaced } from 'features/call/vrs-call/store';
import { wasCallPlacedSelector } from 'features/call/vrs-call/store/vrsCallSelectors';
import { handleError } from 'features/notification/store';
import { resetReportingState } from 'features/reporting/store';
import {
  isAcceptingUserSelector,
  isTeamingStartedSelector,
} from 'features/teaming/teaming-base/store';

export const initialVrsCallCommentsState: VrsCallCommentsState = {
  isReportNeeded: false,
  previousCallId: 0,
  reportingPreviousCallId: 0,
  reportType: ReportType.DEFAULT,
  previousCallReportSource: PreviousCallReportSource.NONE,
  previousCalls: [],
  isLoadingPreviousCalls: false,
};

const vrsCallCommentsSlice = createSlice({
  name: 'vrs-call-comments',
  initialState: initialVrsCallCommentsState,
  reducers: {
    setIsReportNeeded: (state, action: PayloadAction<boolean>) => {
      state.isReportNeeded = action.payload;
    },
    setPreviousCallId: (state, action: PayloadAction<number>) => {
      state.previousCallId = action.payload;
    },
    previousCallToBeReportedSelected: (
      state,
      action: PayloadAction<number>
    ) => {
      state.reportingPreviousCallId = action.payload;
    },
    setReportType: (state, action: PayloadAction<ReportType>) => {
      state.reportType = action.payload;
    },
    startPreviousCallReporting: (
      state,
      action: PayloadAction<PreviousCallReportSource>
    ) => {
      state.previousCallReportSource = action.payload;
    },
    resetCallComments: () => initialVrsCallCommentsState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPreviousCalls.pending, (state) => {
        state.isLoadingPreviousCalls = true;
      })
      .addCase(fetchPreviousCalls.fulfilled, (state, action) => {
        state.isLoadingPreviousCalls = false;
        state.previousCalls = action.payload || [];
      })
      .addCase(fetchPreviousCalls.rejected, (state) => {
        state.isLoadingPreviousCalls = false;
        state.previousCalls = [];
      });
  },
});

export const {
  setIsReportNeeded,
  setPreviousCallId,
  resetCallComments,
  setReportType,
  startPreviousCallReporting,
  previousCallToBeReportedSelected,
} = vrsCallCommentsSlice.actions;

export const vrsCallCommentsReducer = vrsCallCommentsSlice.reducer;

export const closeCallAfterReport = createAsyncThunk(
  'vrsCallComments/closeCallAfterReport',
  async (__, { dispatch, getState }) => {
    const state = getState() as RootState;
    const callId = callIdSelector(state);
    const isTeamingStarted = isTeamingStartedSelector(state);
    const isAcceptingUser = isAcceptingUserSelector(state);
    const wasCallPlaced = wasCallPlacedSelector(state);
    const terminationType = terminationTypeSelector(state);
    const isH2DCall = isHearingToDeafSelector(state);

    if (!callId || (isTeamingStarted && isAcceptingUser)) {
      return;
    }

    try {
      const interpretTime =
        await CallTimerDaoService.getTotalInterpretTime(callId);
      const termType = terminationDeciderVrs({
        interpretTime,
        wasCallPlaced,
        terminationType,
        isH2DCall,
      });
      await dispatch(closeCall({ terminationType: termType }));
    } catch (error) {
      console.error('Error in closeCallAfterReport:', error);
      dispatch(
        handleError({
          error: error as Error,
          message: 'Error in closeCallAfterReport',
        })
      );
      dispatch(handleResetCall());
    } finally {
      dispatch(resetCallWasPlaced());
      dispatch(resetReportingState());
      dispatch(setIsReportNeeded(false));
      dispatch(setPreviousCallId(callId));
      dispatch(resetCallComments());
    }
  }
);
